import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Ornament } from "../components/Ornament";
import LegalFooter from "../components/LegalFooter";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function FoundingBeta() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("individual");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?flow=founding_beta&next=${encodeURIComponent("/founding-beta")}`, { replace: true });
      return;
    }
    setFirstName((user.name || "").split(" ")[0] || "");
    setEmail(user.email || "");
    (async () => {
      try {
        const r = await api.get("/beta/mine");
        if (r.data?.applied) setExisting(r.data.application);
      } catch (e) { /* first-time — no existing application */ }
    })();
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim()) { setError("Please add your first name."); return; }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setError("Please enter a valid email."); return; }
    if (!consent) { setError("Please confirm the consent checkbox to submit."); return; }
    setBusy(true);
    try {
      await api.post("/beta/apply", {
        first_name: firstName.trim(),
        email: email.trim(),
        plan,
        consent: true,
      });
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center paper">
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>
          One moment…
        </p>
      </div>
    );
  }

  const alreadyApplied = !!existing && !sent;

  return (
    <div className="min-h-screen paper" data-testid="founding-beta-page">
      <header className="border-b border-[#F0EAE0] bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="fb-brand-link">
            <span className="text-[#B8973A] text-xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
            <span className="text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", letterSpacing: "0.06em" }}>The Living Book</span>
          </Link>
          <Link to="/dashboard" className="label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="fb-back-link">
            &larr; Back to your book
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        {sent || alreadyApplied ? (
          <div className="text-center" data-testid="fb-success-state">
            <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em] mb-6">APPLICATION RECEIVED</p>
            <h1
              className="text-4xl sm:text-5xl mb-6 text-[#111D33]"
              style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.15 }}
              data-testid="fb-success-headline"
            >
              Thank you. <span style={{ fontStyle: "italic" }}>Beautifully received.</span>
            </h1>
            <Ornament className="my-8" />
            <p className="text-[#1B2A4A] mb-4" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.75 }}>
              Your application to join the Founding Beta has been saved. We&rsquo;re inviting only 20 families, so we&rsquo;ll write to you personally when your invitation is ready.
            </p>
            <p className="text-[#8C8278] italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
              A confirmation email is on its way.
            </p>
            <div className="mt-12">
              <Link to="/story-week" className="btn-outline-navy" data-testid="fb-return-storyweek">
                Return to Story Week
              </Link>
            </div>
          </div>
        ) : (
          <>
            <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em] mb-6">THE FOUNDING BETA</p>
            <h1
              className="text-4xl sm:text-5xl mb-6 text-[#111D33]"
              style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.15 }}
              data-testid="fb-headline"
            >
              Help us shape the <span style={{ fontStyle: "italic" }}>full Living Book.</span>
            </h1>
            <p className="text-[#1B2A4A] mb-10" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.75 }}>
              Story Week is open now. We&rsquo;re inviting 20 families to help test the full Living Book before public launch.
            </p>

            <form onSubmit={submit} className="space-y-6" data-testid="fb-form">
              <div>
                <label htmlFor="fb-first-name" className="block label-ui text-[11px] text-[#8C8278] mb-2 tracking-[0.2em]">FIRST NAME</label>
                <input
                  id="fb-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 hairline-cream bg-white"
                  style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }}
                  data-testid="fb-first-name-input"
                  required
                />
              </div>

              <div>
                <label htmlFor="fb-email" className="block label-ui text-[11px] text-[#8C8278] mb-2 tracking-[0.2em]">EMAIL</label>
                <input
                  id="fb-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 hairline-cream bg-white"
                  style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }}
                  data-testid="fb-email-input"
                  required
                />
              </div>

              <div>
                <p className="block label-ui text-[11px] text-[#8C8278] mb-3 tracking-[0.2em]">MEMBERSHIP</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label
                    className={`p-5 hairline-cream cursor-pointer transition-colors ${plan === "individual" ? "bg-[#FAF7F2]" : "bg-white hover:bg-[#FAF7F2]"}`}
                    style={{ borderRadius: 4, border: plan === "individual" ? "1px solid #B8973A" : undefined }}
                    data-testid="fb-plan-individual"
                  >
                    <input
                      type="radio"
                      name="fb-plan"
                      value="individual"
                      checked={plan === "individual"}
                      onChange={() => setPlan("individual")}
                      className="sr-only"
                    />
                    <p className="chapter-roman mb-2" style={{ fontSize: "0.75rem" }}>I</p>
                    <p className="text-lg mb-1" style={{ fontFamily: "Cormorant Garamond, serif", color: "#111D33" }}>Individual</p>
                    <p className="text-[#8C8278]" style={{ fontFamily: "Georgia, serif", fontSize: "13px" }}>
                      For one storyteller. Founding Member rate: &pound;49/year.
                    </p>
                  </label>
                  <label
                    className={`p-5 hairline-cream cursor-pointer transition-colors ${plan === "family" ? "bg-[#FAF7F2]" : "bg-white hover:bg-[#FAF7F2]"}`}
                    style={{ borderRadius: 4, border: plan === "family" ? "1px solid #B8973A" : undefined }}
                    data-testid="fb-plan-family"
                  >
                    <input
                      type="radio"
                      name="fb-plan"
                      value="family"
                      checked={plan === "family"}
                      onChange={() => setPlan("family")}
                      className="sr-only"
                    />
                    <p className="chapter-roman mb-2" style={{ fontSize: "0.75rem" }}>II</p>
                    <p className="text-lg mb-1" style={{ fontFamily: "Cormorant Garamond, serif", color: "#111D33" }}>Family</p>
                    <p className="text-[#8C8278]" style={{ fontFamily: "Georgia, serif", fontSize: "13px" }}>
                      Up to four Storytellers. Founding Member rate: &pound;89/year.
                    </p>
                  </label>
                </div>
              </div>

              <label className="flex items-start gap-3 pt-2 cursor-pointer" data-testid="fb-consent-label">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                  data-testid="fb-consent-checkbox"
                />
                <span className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "14px", lineHeight: 1.6 }}>
                  I&rsquo;d like to be considered as a Founding Beta tester. I understand testing is free, and if I choose to continue after launch I can keep the founding rate.
                </span>
              </label>

              {error && (
                <p className="text-red-700 text-sm" data-testid="fb-error" style={{ fontFamily: "Georgia, serif" }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                className="btn-gold w-full"
                style={{ minHeight: 52, letterSpacing: "0.2em", fontSize: "13px" }}
                data-testid="fb-submit-btn"
              >
                {busy ? "SENDING…" : "SUBMIT APPLICATION"}
              </button>
            </form>
          </>
        )}
      </main>

      <LegalFooter tone="light" />
    </div>
  );
}
